import { useAppSelector } from "@hypexone/uicore";
import { domain } from "../constants/Branding";
import { getEnvVariable } from "../constants/brandConfig";
import { trackApkDownload } from "./trackApkDownload";

export type BannerDto = {
  id?: number;
  houseId?: number;
  bannerId?: string;
  sportsBookUrl?: string;
  title?: string;
  category?: string;
  deviceType?: string;
  level?: number;
  publicUrl?: string;
  redirectionUrl?: string;
  imageContent?: string;
  displayContent?: string;
  active?: boolean;
  hideInApk?: boolean;
  hide_in_apk?: boolean;
  hideInWeb?: boolean;
  hide_in_web?: boolean;
};

const DOWNLOAD_APP_REDIRECTION = "Download App";

export const isDownloadAppBanner = (banner: BannerDto | null | undefined) =>
  !!banner?.redirectionUrl
    ?.toLowerCase()
    .includes(DOWNLOAD_APP_REDIRECTION.toLowerCase());

/**
 * Returns a click handler that downloads the APK for "Download App" banners.
 * It returns true when it handled the click, so callers can skip their own redirect.
 */
export const useDownloadAppBannerClick = () => {
  const domainConfig = useAppSelector((state) => state.common.domainConfig);
  const apkUrl = getEnvVariable(domain, "apk_url") || domainConfig?.apkUrl;

  return (banner: BannerDto | null | undefined, source: string): boolean => {
    if (!isDownloadAppBanner(banner)) return false;
    if (!apkUrl) return true;
    trackApkDownload(source);
    const link = document.createElement("a");
    link.href = apkUrl.startsWith("http") ? apkUrl : `https://${apkUrl}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  };
};

/**
 * Drop banners marked hideInApk (or "Download App" banners) while in the APK,
 * and hideInWeb while on the web.
 */
export const visibleBanners = <T extends BannerDto>(
  banners: T[] | null | undefined,
  isAndroidWebView: boolean
): T[] => {
  if (!banners?.length) return banners ?? [];
  return banners.filter((banner) =>
    isAndroidWebView
      ? !banner.hideInApk && !banner.hide_in_apk && !isDownloadAppBanner(banner)
      : !banner.hideInWeb && !banner.hide_in_web
  );
};
